import FormInput from "../../common/components/form/FormInput";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    getAddressByZipCode,
    setAddressCity,
    setAddressHouseNumber,
    setAddressHouseNumberAddition,
    setAddressStreet,
    setAddressZipcode,
    setEmail,
    setFirstName,
    setInsertion,
    setLastName,
    setMobile,
} from "./FormSlice";
import { JSX, RefObject, useEffect, useRef, useState } from "react";
import {
    setDamageDate,
    setDamageNumber,
    setGreenCardCode,
} from "../vehicleInformation/vehicleInformationSlice";
import moment from "moment";
import { updateDossier } from "../../app/rootReducer";
import { PatchStepsEnum } from "../../common/constants/PatchStepsEnum";
import { setGeneralStep } from "../helpType/helpTypeSlice";
import { GeneralStepsEnum } from "../../common/constants/GeneralStepsEnum";
import FormInputPhoneNumber from "../../common/components/form/FormInputPhoneNumber";
import { dossierTypeEnum } from "../../common/constants/dossierTypeEnum";
import { fundingTypeEnum } from "../../common/constants/fundingTypeEnum";
import { BovagTermsTypeEnum } from "../../common/constants/BovagTermsTypeEnum";
import { scheduleScrollToStep } from "../../common/helpers/scrollToStep";
import { formCopy } from "./form.copy";
import { PersonInterface } from "../../common/interfaces/PersonInterface";
import { AppDispatch } from "../../app/store";
import {
    conditionalFieldError,
    invalidFieldError,
    isZipCodeFormatValid,
    submitPersonalForm,
    trimmedFieldError,
} from "./formHelpers";

function useFormEffects(params: {
    person: PersonInterface;
    damageDate: string | null | undefined;
    dossier: unknown;
    activeHelpType?: string;
    dispatch: AppDispatch;
    scrollRef: RefObject<HTMLFormElement | null>;
    setValidZipcode: (valid: boolean) => void;
}): void {
    const {
        person,
        damageDate,
        dossier,
        activeHelpType,
        dispatch,
        scrollRef,
        setValidZipcode,
    } = params;
    const isInitialMount = useRef(true);

    useEffect(() => {
        setValidZipcode(isZipCodeFormatValid(person?.address?.zip_code));
    }, [person.address.zip_code, person, setValidZipcode]);

    useEffect(() => {
        if (!isInitialMount.current && damageDate) {
            const delayDebounceFn = setTimeout(() => {
                dispatch(
                    updateDossier({
                        data: { damage_date: damageDate },
                        method: PatchStepsEnum.PatchDamageDate,
                    }),
                );
            }, 800);
            return () => clearTimeout(delayDebounceFn);
        } else {
            isInitialMount.current = false;
        }
    }, [damageDate, dispatch]);

    useEffect(() => {
        if (!dossier) return;
        scheduleScrollToStep(() => scrollRef.current);
    }, [dossier, scrollRef]);

    useEffect(() => {
        if (
            activeHelpType === dossierTypeEnum.GlassReplacement ||
            activeHelpType === dossierTypeEnum.GlassRepair
        ) {
            window.dataLayer.push({
                event: "RuitschadeAanmeldenAanvullend",
            });
        }

        if (activeHelpType === dossierTypeEnum.BodyRepair) {
            window.dataLayer.push({
                event: "AutoschadeAanmeldenAanvullend",
            });
        }

        if (activeHelpType === dossierTypeEnum.CaravanRepair) {
            window.dataLayer.push({
                event: "CamperschadeAanmeldenAanvullend",
            });
        }
    }, [activeHelpType]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (person.address.house_number && person.address.zip_code) {
                dispatch(getAddressByZipCode());
            }
        }, 600);
        return () => {
            clearTimeout(timeout);
        };
    }, [person.address.house_number, person.address.zip_code, dispatch]);
}

interface PersonalDetailsFormProps {
    person: PersonInterface;
    errorForm: boolean;
    validZipcode: boolean;
    zipcodeLoading: boolean;
    greenCardCode?: string;
    fundingType?: string;
    damageDate?: string;
    damageNumber?: string;
    vehicleUsage?: BovagTermsTypeEnum;
    bovagTermsType?: BovagTermsTypeEnum;
    dispatch: AppDispatch;
    scrollRef: RefObject<HTMLFormElement | null>;
    onSubmit: () => void;
}

const PersonalDetailsForm = (props: PersonalDetailsFormProps): JSX.Element => {
    const {
        person,
        errorForm,
        validZipcode,
        zipcodeLoading,
        greenCardCode,
        fundingType,
        damageDate,
        damageNumber,
        vehicleUsage,
        bovagTermsType,
        dispatch,
        scrollRef,
        onSubmit,
    } = props;

    return (
        <form
            className="s-appointment__group border-color-primary"
            id="naw-form"
            ref={scrollRef}
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}
        >
            <h1 className="s-appointment__group__title">{formCopy.title}</h1>

            <div className="c-form__row c-form__row--name">
                <FormInput
                    required={true}
                    name={"first_name"}
                    value={person.first_name}
                    type={"text"}
                    label={formCopy.labels.firstName}
                    error={trimmedFieldError(
                        person?.first_name,
                        errorForm,
                        formCopy.errors.firstName,
                    )}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setFirstName(value.toString()));
                    }}
                />

                <FormInput
                    value={person.insertion}
                    name={"insertion"}
                    type={"text"}
                    label={formCopy.labels.insertion}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setInsertion(value.toString()));
                    }}
                />

                <FormInput
                    required={true}
                    name={"last_name"}
                    value={person.last_name}
                    type={"text"}
                    label={formCopy.labels.lastName}
                    error={trimmedFieldError(
                        person?.last_name,
                        errorForm,
                        formCopy.errors.lastName,
                    )}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setLastName(value.toString()));
                    }}
                />
            </div>

            <div className="c-form__row">
                <FormInput
                    type={"email"}
                    name={"email"}
                    value={person.email}
                    required={true}
                    label={formCopy.labels.email}
                    error={conditionalFieldError(
                        Boolean(person?.email?.includes("@")),
                        errorForm,
                        !person?.email?.includes("@"),
                        formCopy.errors.email,
                    )}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setEmail(value.toString()));
                    }}
                />
            </div>

            <div className="c-form__row">
                <FormInput
                    required={true}
                    name={"zip_code"}
                    value={person.address.zip_code}
                    type={"text"}
                    label={formCopy.labels.zipCode}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setAddressZipcode(value.toString()));
                    }}
                    error={invalidFieldError(
                        errorForm,
                        !validZipcode,
                        formCopy.errors.zipCode,
                    )}
                />
            </div>

            <div className="c-form__row--half">
                <FormInput
                    required={true}
                    name={"house_number"}
                    value={person.address.house_number}
                    type={"number"}
                    label={formCopy.labels.houseNumber}
                    error={invalidFieldError(
                        errorForm,
                        !person.address?.house_number,
                        formCopy.errors.houseNumber,
                    )}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setAddressHouseNumber(Number(value)));
                    }}
                />

                <FormInput
                    name={"house_number_addition"}
                    value={person.address.house_number_addition}
                    type={"text"}
                    label={formCopy.labels.addition}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(
                            setAddressHouseNumberAddition(value.toString()),
                        );
                    }}
                />
            </div>

            <div className="c-form__row">
                <FormInput
                    required={true}
                    name={"street"}
                    value={person.address.street}
                    loading={zipcodeLoading}
                    type={"text"}
                    label={formCopy.labels.street}
                    error={invalidFieldError(
                        errorForm,
                        !person.address?.street?.trim(),
                        formCopy.errors.street,
                    )}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setAddressStreet(value.toString()));
                    }}
                />
            </div>

            <div className="c-form__row">
                <FormInput
                    required={true}
                    type={"text"}
                    name={"city"}
                    value={person.address.city}
                    loading={zipcodeLoading}
                    label={formCopy.labels.city}
                    error={invalidFieldError(
                        errorForm,
                        !person.address?.city?.trim(),
                        formCopy.errors.city,
                    )}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setAddressCity(value.toString()));
                    }}
                />
            </div>

            <div className="c-form__row">
                <FormInputPhoneNumber
                    type={"text"}
                    name={"phone"}
                    value={person.mobile_number}
                    required={true}
                    error={trimmedFieldError(
                        person?.mobile_number,
                        errorForm,
                        formCopy.errors.mobile,
                    )}
                    label={formCopy.labels.mobile}
                    message={formCopy.errors.mobile}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setMobile(value.toString()));
                    }}
                />
            </div>

            {fundingType !== fundingTypeEnum.Lease && (
                <div className="c-form__row">
                    <FormInput
                        type={"text"}
                        modal={"greenCard"}
                        value={greenCardCode}
                        placeholder={formCopy.placeholders.greenCard}
                        label={formCopy.labels.greenCard}
                        required={true}
                        error={trimmedFieldError(
                            greenCardCode,
                            errorForm,
                            formCopy.errors.greenCard,
                        )}
                        onChange={(value) => {
                            dispatch(setGeneralStep(GeneralStepsEnum.Form));
                            dispatch(setGreenCardCode(value.toString()));
                        }}
                    />
                </div>
            )}

            <div className="c-form__row">
                <FormInput
                    type={"date"}
                    onChange={(value) =>
                        dispatch(setDamageDate(value.toString()))
                    }
                    value={damageDate}
                    label={formCopy.labels.damageDate}
                    required={true}
                    maxDate={moment().format("YYYY-MM-DD")}
                    minDate={moment().subtract(1, "year").format("YYYY-MM-DD")}
                />
            </div>

            <div className="c-form__row">
                <FormInput
                    type={"text"}
                    onChange={(value) => {
                        dispatch(setGeneralStep(GeneralStepsEnum.Form));
                        dispatch(setDamageNumber(value.toString()));
                    }}
                    value={damageNumber}
                    label={formCopy.labels.damageNumber}
                    required={false}
                />
            </div>
            <div className="c-form__buttons">
                <button
                    type="submit"
                    className="c-form__buttons__single c-form__buttons__single___primary background-primary"
                    disabled={!bovagTermsType || !vehicleUsage}
                >
                    <span className="font-face-primary">
                        {formCopy.actions.summary}
                    </span>
                </button>
            </div>
        </form>
    );
};

const Form = (): JSX.Element | null => {
    const { dossier } = useAppSelector((state) => state.root);
    const {
        greenCardCode,
        fundingType,
        damageDate,
        damageNumber,
        vehicleUsage,
    } = useAppSelector((state) => state.vehicleInformation);
    const { person, zipcodeLoading } = useAppSelector((state) => state.form);

    const [validZipcode, setValidZipcode] = useState(true);

    const [errorForm, setErrorForm] = useState(false);

    const scrollRef = useRef<HTMLFormElement>(null);

    const helpType = useAppSelector((state) => state.helpType);
    const { activeHelpType } = helpType;

    const dispatch = useAppDispatch();

    useFormEffects({
        person,
        damageDate,
        dossier,
        activeHelpType,
        dispatch,
        scrollRef,
        setValidZipcode,
    });

    if (!dossier) {
        return null;
    }

    return (
        <PersonalDetailsForm
            person={person}
            errorForm={errorForm}
            validZipcode={validZipcode}
            zipcodeLoading={zipcodeLoading}
            greenCardCode={greenCardCode}
            fundingType={fundingType}
            damageDate={damageDate}
            damageNumber={damageNumber}
            vehicleUsage={vehicleUsage}
            bovagTermsType={dossier.bovag_terms_type}
            dispatch={dispatch}
            scrollRef={scrollRef}
            onSubmit={() =>
                submitPersonalForm({
                    damageDate,
                    person,
                    dispatch,
                    setErrorForm,
                })
            }
        />
    );
};

export default Form;
